import { useCallback, useEffect, useRef, useState } from "react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Portal from "@mui/material/Portal";
import Tooltip, {
  tooltipClasses,
  type TooltipProps,
} from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import StarsIcon from "@mui/icons-material/Stars";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { RouteLink } from "@/components/common/styles";
import { useSession } from "@/security/RouteGuards";
import useAccountLogout from "@/hooks/useAccountLogout";
import useVerificationActionRequired from "@/hooks/useVerificationActionRequired";
import { getFromStorage, setToStorage } from "@/utils/localStorageUtils";
import { LocalStorageKeys } from "@/config/localStorageKeys";

interface AccountMenuProps {
  /** Called after a menu item is chosen (e.g. to close the mobile drawer). */
  onNavigate?: () => void;
}

/** Account button width on desktop; also the dropdown's min width (see below). */
const ACCOUNT_BUTTON_WIDTH = 135;

/**
 * When the Settings-in-app-bar relocation shipped. Only accounts created before
 * this saw the old sidebar location, so only they get the one-time relocation
 * coach mark; accounts created afterwards never knew the old spot.
 *
 * NB: this is a FIXED cutoff, not deployment/build time — build time drifts
 * forward on every deploy, which would wrongly re-flag users created after the
 * feature but before a later deploy.
 */
//TODO: Remove once Account Settings location change notification is not needed anymore
const SETTINGS_RELOCATED_AT = new Date("2026-07-22T00:00:00Z");

/**
 * Account dropdown in the top app bar. Replaces the old email/Settings dropdown
 * that used to live at the top of the sidebar drawer (R-1706).
 *
 * Thin orchestrator: owns the open/coach-mark state and handlers, and composes
 * the trigger button, the one-time relocation coach mark ({@link CoachMarkTooltip}
 * + {@link CoachMarkContent} + {@link SpotlightBackdrop}), and the
 * {@link AccountMenuList} dropdown. Those pieces are defined below.
 */
export default function AccountMenu({ onNavigate }: AccountMenuProps) {
  const { profile } = useSession();
  const handleLogout = useAccountLogout();
  const verificationActionRequired = useVerificationActionRequired();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Existing users (created before the relocation) get the coach mark once;
  // newer accounts never saw the old location, so they never see it.
  const isExistingUser =
    profile?.created_at != null &&
    new Date(profile.created_at) < SETTINGS_RELOCATED_AT;

  const [coachMarkOpen, setCoachMarkOpen] = useState(
    () =>
      isExistingUser &&
      getFromStorage(LocalStorageKeys.AccountMenuMovedCoachMarkSeen) !== "true"
  );

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);

  // Track the Account button's viewport rect so the spotlight overlay can cut a
  // transparent hole over it. Measured after paint and kept in sync on resize;
  // the button sits in the fixed app bar, so no scroll handling is needed.
  useEffect(() => {
    if (!coachMarkOpen) {
      setSpotlightRect(null);
      return;
    }
    const measure = () => {
      if (buttonRef.current) {
        setSpotlightRect(buttonRef.current.getBoundingClientRect());
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [coachMarkOpen]);

  const dismissCoachMark = useCallback(() => {
    setCoachMarkOpen(false);
    setToStorage(LocalStorageKeys.AccountMenuMovedCoachMarkSeen, "true");
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    dismissCoachMark();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = () => {
    handleClose();
    onNavigate?.();
  };

  return (
    <>
      {/*
        Trigger stays inline: the button is the CoachMarkTooltip's single child
        (MUI requirement) and also carries `buttonRef`, which the parent needs
        for the spotlight measurement and the dropdown's width.
      */}
      <CoachMarkTooltip
        open={coachMarkOpen}
        arrow
        placement="bottom-end"
        title={<CoachMarkContent onDismiss={dismissCoachMark} />}
      >
        <Badge
          color="error"
          variant="dot"
          overlap="circular"
          invisible={!verificationActionRequired}
        >
          <Button
            ref={buttonRef}
            color="neutral"
            sx={{
              width: {
                xs: "72px",
                sm: ACCOUNT_BUTTON_WIDTH,
              },
              padding: {
                xs: "10px 16px",
                sm: "8px 16px",
              },
              height: "40px",
              color: "text.primary",
              // On mobile the "Account" label is hidden, leaving the account +
              // chevron icons adjacent. MUI's default 8px start-margin + 8px
              // end-margin would space them 16px apart; zero the start icon's
              // right margin on xs so the end icon's own 8px left margin is the
              // whole gap (design spec). Restore 8px on sm+ where the label sits
              // between the two icons.
              "& .MuiButton-startIcon": { mr: { xs: 0, sm: "8px" } },
            }}
            variant="outlined"
            onClick={handleOpen}
            data-testid="account-menu-button"
            aria-label="Account"
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            startIcon={<AccountCircleIcon sx={{ color: "text.primary" }} />}
            endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          >
            {/* Label hidden on mobile — icon + chevron only (R-1706 spec). */}
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              Account
            </Box>
          </Button>
        </Badge>
      </CoachMarkTooltip>

      {coachMarkOpen && spotlightRect ? (
        <SpotlightBackdrop rect={spotlightRect} onDismiss={dismissCoachMark} />
      ) : null}

      <AccountMenuList
        anchorEl={anchorEl}
        open={open}
        width={buttonRef.current?.offsetWidth}
        isVip={Boolean(profile?.is_vip_customer)}
        onClose={handleClose}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
      />
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Pieces composed by AccountMenu above, in the order they are used.
   ────────────────────────────────────────────────────────────────────────── */

/** Light card tooltip used for the one-time relocation coach mark. */
const CoachMarkTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => {
  // The coach mark is intentionally inverted relative to the app theme: a dark
  // bubble with light text in light mode, and a light bubble with dark text in
  // dark mode. Text inherits `foreground` (see the Typography `color="inherit"`
  // below); the "Got it" button keeps its own contained styling.
  //
  // NB: use a fixed dark surface + common.white, NOT the grey scale — this
  // theme inverts `grey[*]` in dark mode, which would flip the text back and
  // make it invisible (light text on the light bubble).
  const darkSurface = "#353535";
  const isLight = theme.palette.mode === "light";
  const background = isLight ? darkSurface : theme.palette.common.white;
  const foreground = isLight ? theme.palette.common.white : darkSurface;

  return {
    [`& .${tooltipClasses.arrow}`]: {
      color: background,
      transform: "translateX(256px) !important",
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: background,
      color: foreground,
      boxShadow: theme.shadows[6],
      borderRadius: 4,
      padding: 0,
      fontSize: theme.typography.pxToRem(18),
    },
  };
});

/** Body of the relocation coach mark (title + copy + dismiss button). */
function CoachMarkContent({ onDismiss }: { onDismiss: () => void }) {
  return (
    <Box sx={{ p: 3, mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2" fontWeight={400} color="inherit">
          Account Menu Updated
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="inherit"
        sx={{ mt: 0.5, opacity: 0.7 }}
      >
        Account settings are now available from the Account menu in the
        top-right corner.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={onDismiss}
        >
          Got it
        </Button>
      </Box>
    </Box>
  );
}

/** Bright halo kept around the Account button inside the spotlight cut-out. */
const SPOTLIGHT_PADDING = 4;

/** Corner radius of the spotlight cut-out, in px. */
const SPOTLIGHT_RADIUS = 4;

/**
 * Full-screen dim with a rounded-rect hole punched over the Account button
 * (+ SPOTLIGHT_PADDING) via clip-path. Unlike a box-shadow spotlight, this is a
 * real hit-testable surface: with default pointer events it BLOCKS clicks from
 * reaching the page underneath, while the clipped-out hole is skipped for both
 * paint and hit-testing, so clicks there fall through to the button at full
 * brightness.
 *
 * `path(evenodd, …)` is used instead of `polygon(…)` because polygon draws only
 * straight edges; the arc (`A`) commands round the hole's corners. path() needs
 * absolute px (no %), so the outer rect spans the current viewport — the caller
 * re-measures `rect` on resize, keeping hole and viewport in sync. Clicking the
 * dim dismisses the coach mark; the tooltip is portaled above, so it stays
 * interactive.
 */
function SpotlightBackdrop({
  rect,
  onDismiss,
}: {
  rect: DOMRect;
  onDismiss: () => void;
}) {
  const left = rect.left - SPOTLIGHT_PADDING;
  const right = rect.right + SPOTLIGHT_PADDING;
  const top = rect.top - SPOTLIGHT_PADDING;
  const bottom = rect.bottom + SPOTLIGHT_PADDING;
  // A radius can't exceed half the hole's smallest side.
  const r = Math.min(SPOTLIGHT_RADIUS, (right - left) / 2, (bottom - top) / 2);
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Outer viewport rect + inner rounded-rect hole; evenodd cuts it out.
  const hole = [
    `M${left + r} ${top}`,
    `H${right - r}`,
    `A${r} ${r} 0 0 1 ${right} ${top + r}`,
    `V${bottom - r}`,
    `A${r} ${r} 0 0 1 ${right - r} ${bottom}`,
    `H${left + r}`,
    `A${r} ${r} 0 0 1 ${left} ${bottom - r}`,
    `V${top + r}`,
    `A${r} ${r} 0 0 1 ${left + r} ${top}`,
    "Z",
  ].join(" ");
  const clipPath = `path(evenodd, "M0 0 H${vw} V${vh} H0 Z ${hole}")`;

  return (
    <Portal>
      <Box
        aria-hidden
        onClick={onDismiss}
        style={{ clipPath }}
        sx={(theme) => ({
          position: "fixed",
          inset: 0,
          zIndex: theme.zIndex.modal,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        })}
      />
    </Portal>
  );
}

interface AccountMenuListProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  /** Width of the anchor button, so the dropdown matches it. */
  width?: number;
  isVip: boolean;
  onClose: () => void;
  onNavigate: () => void;
  onLogout: () => void;
}

/** The dropdown anchored under the Account button. */
function AccountMenuList({
  anchorEl,
  open,
  width,
  isVip,
  onClose,
  onNavigate,
  onLogout,
}: AccountMenuListProps) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      // Align the menu flush under the button's right edge, even on mobile where
      // the button sits near the viewport edge (default 16px threshold would
      // otherwise nudge it inward and break the alignment).
      marginThreshold={0}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        list: { "aria-labelledby": "account-menu-button" },
        // `width` matches the anchor button; the caller reads it from a ref
        // (not anchorEl, which nulls on close) so it stays stable through the
        // close animation instead of collapsing. minWidth floors it at the
        // desktop button width so the mobile icon-only button doesn't produce a
        // cramped dropdown.
        paper: { sx: { width, minWidth: ACCOUNT_BUTTON_WIDTH } },
      }}
    >
      <RouteLink to="/profile" data-testid="settings-menu-item">
        <MenuItem onClick={onNavigate}>
          <SettingsOutlinedIcon sx={{ pr: 2 }} />
          Settings
        </MenuItem>
      </RouteLink>
      {isVip ? (
        <RouteLink to="/profile/vip">
          <MenuItem onClick={onNavigate} component="a">
            <StarsIcon sx={{ pr: 2 }} />
            VIP Member
          </MenuItem>
        </RouteLink>
      ) : null}
      {/* Divider via borderTop (not <Divider/>) per the house sub-pixel rule. */}
      <MenuItem
        onClick={onLogout}
        sx={{ color: "error.main", borderTop: 1, borderColor: "divider" }}
      >
        <LogoutOutlinedIcon sx={{ pr: 2 }} />
        Logout
      </MenuItem>
    </Menu>
  );
}
